/**
 * OutputsBackendConfig — the editable, per-backend specialisation of the Outputs
 * panel (backends-spec §4) plus the named-preset bar (§5).
 *
 * Layout: ONE preset bar (save-as / restore / rename / delete, per-backend
 * namespace) on top, then a per-backend config section:
 *   - MIDI  → output-port picker, number-of-CCs, and device templates; each
 *             output card owns its CC#/channel/name fields.
 *   - OSC   → bridge URL + connect status + send-raw toggle, per-output path/range.
 *   - VCV/CV→ bridge URL + connect status + send-raw toggle, per-output polarity
 *             (uni 0–10 V / bipolar ±5 V). The browser drives + trains the VCV
 *             module over the same Deno bridge.
 *   - Synth/Particle/Editor → handled by ModeConfig in Drawers (no extra config here).
 *
 * Everything is editable inline; writes go through the shared MFParam store
 * (ctx.setParam) — never a second data path. This is the ONLY per-backend
 * config editor (BackendAdvanced.tsx, a full-depth duplicate that rendered
 * alongside this one in the same expanded drawer, was deleted 2026-07 —
 * simplification audit S18).
 */
import { useEffect, useState } from 'react';
import type { ConsoleCtx } from '../console/types';
import type { BackendId, CvChannelId } from './output-state';
import { CV_CHANNELS, defaultCvSpec, defaultOscSpec, defaultVcvSpec } from './output-state';
import {
  applyPreset,
  deletePreset,
  getPreset,
  listPresets,
  renamePreset,
  savePreset,
  type OutputPreset,
} from '../backends/presets';
import { MIDI_DEVICES, MIDI_DEVICES_BY_ID, type MidiDeviceParam } from '../midi-devices';

function num(s: string, fallback: number): number {
  const v = parseFloat(s);
  return Number.isFinite(v) ? v : fallback;
}

const cellInput: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-1)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-1)',
  color: 'var(--fg)',
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--fs-xs)',
  padding: '3px 6px',
  boxSizing: 'border-box',
};

const btn = (color: string): React.CSSProperties => ({
  fontSize: 'var(--fs-xs)',
  fontFamily: 'var(--font-mono)',
  padding: '3px 9px',
  cursor: 'pointer',
  borderRadius: 'var(--r-pill)',
  border: `1px solid ${color}`,
  background: 'transparent',
  color,
});

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--fg-dim)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        marginTop: 'var(--sp-2)',
      }}
    >
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--fg-dim)',
        padding: '4px 6px',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {children}
    </th>
  );
}

// ---- Named-preset bar (backends-spec §5) -----------------------------------

function PresetBar({ ctx, backend }: { ctx: ConsoleCtx; backend: BackendId }) {
  const [presets, setPresets] = useState<OutputPreset[]>([]);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState('');

  const refresh = () => setPresets(listPresets(backend));
  useEffect(() => {
    refresh();
    setSelected('');
    setName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend]);

  const backendSettings = (): Record<string, unknown> => {
    if (backend === 'midi') return { outputId: ctx.midiOutputId, ccCount: ctx.midiCcCount };
    if (backend === 'osc') return { url: ctx.oscUrl, sendRaw: ctx.oscSendRaw };
    if (backend === 'vcv') return { url: ctx.vcvUrl, sendRaw: ctx.vcvSendRaw };
    return {};
  };

  const applySettings = (s?: Record<string, unknown>) => {
    if (!s) return;
    if (backend === 'midi') {
      if ('outputId' in s) ctx.setMidiOutputId((s.outputId as string | null) ?? null);
      if ('ccCount' in s) ctx.setMidiCcCount(Number(s.ccCount) || ctx.midiCcCount);
    } else if (backend === 'osc') {
      if ('url' in s) ctx.setOscUrl(String(s.url));
      if ('sendRaw' in s) ctx.setOscSendRaw(Boolean(s.sendRaw));
    } else if (backend === 'vcv') {
      if ('url' in s) ctx.setVcvUrl(String(s.url));
      if ('sendRaw' in s) ctx.setVcvSendRaw(Boolean(s.sendRaw));
    }
  };

  const doSave = () => {
    const n = name.trim();
    if (!n) return;
    savePreset(backend, n, ctx.params, backendSettings());
    refresh();
    setSelected(n);
  };
  const doRestore = (n: string) => {
    const p = getPreset(backend, n);
    if (!p) return;
    ctx.setParams(applyPreset(ctx.params, p));
    applySettings(p.settings);
  };
  const doDelete = () => {
    if (!selected) return;
    deletePreset(backend, selected);
    refresh();
    setSelected('');
  };
  const doRename = () => {
    const to = name.trim();
    if (!selected || !to) return;
    if (renamePreset(backend, selected, to)) {
      refresh();
      setSelected(to);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
        padding: '6px 8px',
        background: 'var(--bg-2)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-1)',
      }}
    >
      <span style={{ fontSize: 9, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Presets · {backend}
      </span>
      <input
        style={{ ...cellInput, width: 120, flex: '0 0 auto' }}
        placeholder="preset name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button type="button" style={btn('var(--accent)')} onClick={doSave} disabled={!name.trim()}>
        Save as
      </button>
      <select
        value={selected}
        onChange={(e) => {
          setSelected(e.target.value);
          if (e.target.value) doRestore(e.target.value);
        }}
        style={{ ...cellInput, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }}
      >
        <option value="">restore…</option>
        {presets.map((p) => (
          <option key={p.name} value={p.name}>
            {p.name}
          </option>
        ))}
      </select>
      <button type="button" style={btn('var(--fg-mute)')} onClick={doRename} disabled={!selected || !name.trim()}>
        Rename
      </button>
      <button type="button" style={btn('var(--danger)')} onClick={doDelete} disabled={!selected}>
        Delete
      </button>
    </div>
  );
}

// ---- Device template picker (synth-midi-cc templates) ----------------------

/**
 * Pick an external synth (e.g. Moog Sub 37), see its parameters BY NAME, tick the
 * ones to control, and "Apply" — this fills the per-output card fields (CC#,
 * channel, name) from the device template and sets the CC count. Pure local state + the
 * existing setParam/setMidiCcCount; the applied result lives in the shared params
 * store, so it is saved/restored by the named-preset bar like any other config.
 */
function DeviceTemplatePicker({ ctx }: { ctx: ConsoleCtx }) {
  const [deviceId, setDeviceId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const device = deviceId ? MIDI_DEVICES_BY_ID[deviceId] : undefined;
  const limit = ctx.params.length; // engine output count caps how many CCs we can drive

  const pickDevice = (id: string) => {
    setDeviceId(id);
    setSelected(new Set());
  };
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectFirst = (n: number) => {
    if (!device) return;
    setSelected(new Set(device.params.slice(0, n).map((p) => p.id)));
  };

  const apply = () => {
    if (!device) return;
    const chosen = device.params.filter((p) => selected.has(p.id)).slice(0, limit);
    if (chosen.length === 0) return;
    ctx.setMidiCcCount(chosen.length);
    chosen.forEach((p, i) =>
      ctx.setParam(i, {
        name: p.label,
        midi: { cc: p.cc, channel: device.default_channel, name: p.label, value: 0 },
      }),
    );
  };

  // Stable, first-seen-order grouping for the checklist.
  const groups: { group: string; params: MidiDeviceParam[] }[] = [];
  if (device) {
    const byGroup = new Map<string, MidiDeviceParam[]>();
    for (const p of device.params) {
      if (!byGroup.has(p.group)) {
        const arr: MidiDeviceParam[] = [];
        byGroup.set(p.group, arr);
        groups.push({ group: p.group, params: arr });
      }
      byGroup.get(p.group)!.push(p);
    }
  }

  const willApply = device ? Math.min(selected.size, limit) : 0;
  const overflow = selected.size > limit;

  return (
    <>
      <SectionLabel>Device template</SectionLabel>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={deviceId}
          onChange={(e) => pickDevice(e.target.value)}
          style={{ ...cellInput, width: 'auto', cursor: 'pointer' }}
        >
          <option value="">— manual (no template) —</option>
          {MIDI_DEVICES.map((d) => (
            <option key={d.device_id} value={d.device_id}>
              {d.name} · {d.params.length} params
            </option>
          ))}
        </select>
        {device && (
          <>
            <button type="button" style={btn('var(--fg-mute)')} onClick={() => selectFirst(Math.min(limit, device.params.length))}>
              Select first {Math.min(limit, device.params.length)}
            </button>
            <button type="button" style={btn('var(--fg-mute)')} onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <button type="button" style={btn('var(--accent)')} onClick={apply} disabled={willApply === 0}>
              Apply {willApply} → outputs
            </button>
          </>
        )}
      </div>
      {device && (
        <>
          <p style={{ fontSize: 9, color: overflow ? 'var(--warn)' : 'var(--fg-dim)', margin: 0, lineHeight: 1.6 }}>
            {device.manufacturer} · default ch {device.default_channel} · {selected.size} selected
            {overflow ? ` (only the first ${limit} will map — engine has ${limit} outputs)` : ''}. Tick the parameters
            the model should drive, then Apply. You can still tweak CC#/channel below.
          </p>
          <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r-1)', padding: '4px 6px' }}>
            {groups.map(({ group, params }) => (
              <div key={group} style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 9, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 2px' }}>
                  {group}
                </div>
                {params.map((p) => (
                  <label
                    key={p.id}
                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)', padding: '1px 0', cursor: 'pointer' }}
                  >
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    <span style={{ flex: 1 }}>{p.label}</span>
                    <span style={{ color: 'var(--fg-mute)', fontFamily: 'var(--font-mono)' }}>CC {p.cc}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ---- MIDI config (backends-spec §2.3 / §4.1) -------------------------------

function MidiConfig({ ctx }: { ctx: ConsoleCtx }) {
  const s = ctx.backendStatus;
  const statusColor =
    s.state === 'ready' ? 'var(--good)' : s.state === 'error' || s.state === 'unavailable' ? 'var(--danger)' : 'var(--warn)';
  return (
    <>
      <SectionLabel>MIDI output</SectionLabel>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={ctx.midiOutputId ?? ''}
          onChange={(e) => ctx.setMidiOutputId(e.target.value || null)}
          onFocus={ctx.refreshMidiPorts}
          style={{ ...cellInput, width: 'auto', cursor: 'pointer' }}
        >
          <option value="">— pick output port —</option>
          {ctx.midiPorts.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 9, color: statusColor }}>{s.message}</span>
      </div>

      <DeviceTemplatePicker ctx={ctx} />

    </>
  );
}

// ---- OSC config (backends-spec §2.4 / §4.2) --------------------------------

function OscConfig({ ctx }: { ctx: ConsoleCtx }) {
  const s = ctx.backendStatus;
  const statusColor = s.state === 'ready' ? 'var(--good)' : s.state === 'connecting' ? 'var(--warn)' : 'var(--danger)';
  const [draftUrl, setDraftUrl] = useState(ctx.oscUrl);
  useEffect(() => setDraftUrl(ctx.oscUrl), [ctx.oscUrl]);
  return (
    <>
      <SectionLabel>OSC bridge</SectionLabel>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...cellInput, width: 200 }}
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          onBlur={() => ctx.setOscUrl(draftUrl)}
          placeholder="ws://localhost:8765"
        />
        <button type="button" style={btn('var(--accent)')} onClick={() => ctx.setOscUrl(draftUrl)}>
          Connect
        </button>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)', color: 'var(--fg-dim)' }}>
          <input type="checkbox" checked={ctx.oscSendRaw} onChange={(e) => ctx.setOscSendRaw(e.target.checked)} />
          send raw 0..1
        </label>
        <span style={{ fontSize: 9, color: statusColor }}>{s.message}</span>
      </div>
      <SectionLabel>Per-output address · physical range</SectionLabel>
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>OSC path</Th>
              <Th>range min</Th>
              <Th>range max</Th>
            </tr>
          </thead>
          <tbody>
            {ctx.params.slice(0, ctx.displayOutputCount).map((p, i) => {
              const o = p.osc ?? defaultOscSpec(p.name);
              return (
                <tr key={i}>
                  <td style={{ padding: '3px 6px' }}>
                    <input
                      style={cellInput}
                      value={o.path}
                      onChange={(e) => ctx.setParam(i, { osc: { ...o, path: e.target.value } })}
                    />
                  </td>
                  <td style={{ padding: '3px 6px', width: 90 }}>
                    <input
                      type="number"
                      style={cellInput}
                      value={o.rangeMin}
                      onChange={(e) => ctx.setParam(i, { osc: { ...o, rangeMin: num(e.target.value, o.rangeMin) } })}
                    />
                  </td>
                  <td style={{ padding: '3px 6px', width: 90 }}>
                    <input
                      type="number"
                      style={cellInput}
                      value={o.rangeMax}
                      onChange={(e) => ctx.setParam(i, { osc: { ...o, rangeMax: num(e.target.value, o.rangeMax) } })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- VCV / CV config (backends-spec §2.6 / §4.3) ---------------------------

function VcvConfig({ ctx }: { ctx: ConsoleCtx }) {
  const s = ctx.backendStatus;
  const statusColor = s.state === 'ready' ? 'var(--good)' : s.state === 'connecting' ? 'var(--warn)' : 'var(--danger)';
  const [draftUrl, setDraftUrl] = useState(ctx.vcvUrl);
  useEffect(() => setDraftUrl(ctx.vcvUrl), [ctx.vcvUrl]);
  return (
    <>
      <SectionLabel>VCV bridge</SectionLabel>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...cellInput, width: 200 }}
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          onBlur={() => ctx.setVcvUrl(draftUrl)}
          placeholder="ws://localhost:8765"
        />
        <button type="button" style={btn('var(--accent)')} onClick={() => ctx.setVcvUrl(draftUrl)}>
          Connect
        </button>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)', color: 'var(--fg-dim)' }}>
          <input type="checkbox" checked={ctx.vcvSendRaw} onChange={(e) => ctx.setVcvSendRaw(e.target.checked)} />
          send raw 0..1
        </label>
        <span style={{ fontSize: 9, color: statusColor }}>{s.message}</span>
      </div>
      <SectionLabel>Per-output polarity</SectionLabel>
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>output</Th>
              <Th>range</Th>
            </tr>
          </thead>
          <tbody>
            {ctx.params.slice(0, ctx.displayOutputCount).map((p, i) => {
              const v = p.vcv ?? defaultVcvSpec();
              return (
                <tr key={i}>
                  <td style={{ padding: '3px 6px', fontSize: 'var(--fs-xs)', color: 'var(--fg-dim)' }}>{p.name}</td>
                  <td style={{ padding: '3px 6px', width: 110 }}>
                    <button
                      type="button"
                      onClick={() => ctx.setParam(i, { vcv: { bipolar: !v.bipolar } })}
                      style={{
                        ...btn(v.bipolar ? 'var(--danger)' : 'var(--fg-mute)'),
                        width: '100%',
                      }}
                    >
                      {v.bipolar ? '±5 V' : '0–10 V'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- uSEQ CV / gate config (backends-spec §2.5; docs/specs/useq-cv-protocol.md) -----------

function CvConfig({ ctx }: { ctx: ConsoleCtx }) {
  const s = ctx.backendStatus;
  const statusColor =
    s.state === 'ready' ? 'var(--good)' : s.state === 'connecting' ? 'var(--warn)' : 'var(--danger)';
  return (
    <>
      <SectionLabel>uSEQ device (USB serial)</SectionLabel>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={btn('var(--accent)')} onClick={ctx.cvConnect}>
          Connect device
        </button>
        <button type="button" style={btn('var(--fg-mute)')} onClick={ctx.cvIdentify}>
          Identify (flash LEDs)
        </button>
        <button type="button" style={btn('var(--danger)')} onClick={ctx.cvDisconnect}>
          Disconnect
        </button>
        <span style={{ fontSize: 9, color: statusColor }}>{s.message}</span>
      </div>
      <SectionLabel>Per-output channel · gate threshold</SectionLabel>
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>Output</Th>
              <Th>uSEQ channel</Th>
              <Th>Gate ≥</Th>
            </tr>
          </thead>
          <tbody>
            {ctx.params.slice(0, ctx.displayOutputCount).map((p, i) => {
              const c = (p.cv as { channel: CvChannelId; gateThreshold: number } | undefined) ?? defaultCvSpec(i);
              const isGate = c.channel.startsWith('gate');
              return (
                <tr key={i}>
                  <td style={{ padding: '3px 6px', fontSize: 'var(--fs-xs)', color: 'var(--fg-dim)' }}>{p.name}</td>
                  <td style={{ padding: '3px 6px', width: 150 }}>
                    <select
                      value={c.channel}
                      onChange={(e) => ctx.setParam(i, { cv: { ...c, channel: e.target.value as CvChannelId } })}
                      style={{ ...cellInput, cursor: 'pointer' }}
                    >
                      <option value="none">— none —</option>
                      {CV_CHANNELS.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '3px 6px', width: 80 }}>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      disabled={!isGate}
                      style={{ ...cellInput, opacity: isGate ? 1 : 0.4 }}
                      value={c.gateThreshold}
                      onChange={(e) =>
                        ctx.setParam(i, {
                          cv: { ...c, gateThreshold: Math.max(0, Math.min(1, num(e.target.value, c.gateThreshold))) },
                        })
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- Public entry ----------------------------------------------------------

export interface OutputsBackendConfigProps {
  ctx: ConsoleCtx;
  backend: BackendId;
}

/** The specialised, editable per-backend config + preset bar for the Outputs panel. */
export function OutputsBackendConfig({ ctx, backend }: OutputsBackendConfigProps) {
  // MIDI / OSC / VCV / CV carry a config + preset surface here; synth/particle/editor
  // config is rendered by ModeConfig in Drawers. The full-depth BackendAdvanced
  // modal reuses the same per-channel sections.
  if (backend !== 'midi' && backend !== 'osc' && backend !== 'vcv' && backend !== 'cvgate') return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <PresetBar ctx={ctx} backend={backend} />
      {backend === 'midi' ? (
        <MidiConfig ctx={ctx} />
      ) : backend === 'osc' ? (
        <OscConfig ctx={ctx} />
      ) : backend === 'vcv' ? (
        <VcvConfig ctx={ctx} />
      ) : (
        <CvConfig ctx={ctx} />
      )}
    </div>
  );
}

export { PresetBar as OutputPresetBar };

/** Tiny status pill for the Outputs drawer header. */
export function BackendStatusChip({ ctx }: { ctx: ConsoleCtx }) {
  const s = ctx.backendStatus;
  if (s.state === 'idle') return null;
  const color =
    s.state === 'ready'
      ? 'var(--good)'
      : s.state === 'error' || s.state === 'unavailable'
        ? 'var(--danger)'
        : 'var(--warn)';
  return (
    <span
      style={{
        fontSize: 9,
        fontFamily: 'var(--font-mono)',
        color,
        border: `1px solid ${color}`,
        borderRadius: 'var(--r-pill)',
        padding: '2px 8px',
      }}
      title={s.message}
    >
      {s.message}
    </span>
  );
}
